import React, { useEffect, useRef, useState } from "react";
import { searchTickers } from "../api";

export default function TickerSearch({ onSelect }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setOpen(false);
      return undefined;
    }
    setLoading(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      searchTickers(query.trim())
        .then((data) => {
          setResults(data.results || []);
          setOpen(true);
          setHighlighted(-1);
        })
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectResult = (result) => {
    onSelect(result.symbol);
    setQuery("");
    setResults([]);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const pick = highlighted >= 0 ? results[highlighted] : results[0];
      if (pick) selectResult(pick);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative w-full max-w-md">
      <div className="relative">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2"
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2"
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          onKeyDown={handleKeyDown}
          placeholder="Search stocks, ETFs..."
          className="w-full pl-9 pr-3 py-2 rounded-lg text-sm"
          style={{ backgroundColor: "var(--bg-nested)", border: "1px solid var(--border-color)", color: "var(--text-primary)", outline: "none" }}
        />
      </div>

      {open && (
        <div
          className="absolute left-0 right-0 mt-1 rounded-lg overflow-hidden z-50"
          style={{ backgroundColor: "var(--bg-card)", border: "1px solid var(--border-color)" }}
        >
          {loading && (
            <div className="px-3 py-2 text-xs" style={{ color: "var(--text-muted)" }}>Searching...</div>
          )}
          {!loading && results.length === 0 && (
            <div className="px-3 py-2 text-xs" style={{ color: "var(--text-muted)" }}>No matches for "{query}"</div>
          )}
          {!loading && results.map((r, i) => (
            <button
              key={r.symbol}
              onClick={() => selectResult(r)}
              onMouseEnter={() => setHighlighted(i)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors"
              style={{ backgroundColor: i === highlighted ? "var(--bg-hover)" : "transparent" }}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-semibold shrink-0" style={{ color: "var(--text-primary)" }}>{r.symbol}</span>
                <span className="truncate" style={{ color: "var(--text-muted)" }}>{r.name}</span>
              </div>
              {r.type === "ETF" && (
                <span
                  className="text-xs px-1.5 py-0.5 rounded shrink-0"
                  style={{ backgroundColor: "var(--bg-blue-soft)", color: "var(--link-blue)" }}
                >
                  ETF
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
